goog.module('yext.autocomplete.Autocomplete');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const helpers = goog.require('yext.helpers');
/** @suppress {extraRequire} */
goog.require('yext.plugins.inputchange');

/**
 * An autocomplete module
 *
 * @author MattJS (matt@yext.com)
 * @constructor
 * @param {!Object} configuration
 *
 * A configuration is required for proper setup of this module. This object must at minimum have
 * $input, $target and source properties. The $input is the <input> form object for which
 * autocompletion will be triggered while $target is where the <ul> containing results will be
 * placed. The source property is a function that will be called with two arguments:
 * (request, response). request is the search string and response a function that will be called
 * by the implementer after the search is complete. The response function must be called with an
 * array of objects who's format must minimally match {label: '...', value: ...}.
 *
 * $activeRegion allows for an larger region to be considered active then just the $input and the
 * $target. This will allow the menu to stay open for clicks inside $activeRegion while forcing it
 * to close for clicks outside $activeRegion.
 */
const Autocomplete = function(configuration) {
  this.$input = configuration.$input;
  this.$target = configuration.$target;
  this.$activeRegion = configuration.$activeRegion || configuration.$target;

  this.source = configuration.source;
  this.searchText = undefined;
  this.keyboardMode = false;
  Autocomplete.REGISTRY.push(this);

  this.bindings();
};
// Stores all copies of Autocomplete to take global action (i.e. closeAllOthers)
Autocomplete.REGISTRY = [];

// This ID is used with 'aria-activedescendant' attribute for accessibility
Autocomplete.ACTIVE_ITEM_ID = 'autocomplete-active-item';

Autocomplete.RESULTS_CONTAINER =
  $('<ul>', {
    'class': 'autocomplete-results-container',
    'role': 'listbox',
    'aria-activedescendant': Autocomplete.ACTIVE_ITEM_ID,
  });

Autocomplete.SEARCH_RESULT =
  $('<li>', {
    'role': 'option',
  });

Autocomplete.NO_RESULTS =
  $('<div>', {
    'class': 'autocomplete-results-message',
  }).text(yext.msg('No matching results'));

Autocomplete.LOADING_RESULTS =
  $('<div>', {
    'class': 'autocomplete-results-message',
  }).text(yext.msg('Loading...'));

Autocomplete.NO_MORE =
  $('<div>', {
    'class': 'autocomplete-results-message',
  }).text(yext.msg('No more results'));

Autocomplete.ACTIVE_KEY_CODES =
  [$.ui.keyCode.UP, $.ui.keyCode.DOWN, $.ui.keyCode.ENTER];

/**
 * Bind the input field
 */
Autocomplete.prototype.bindings = function() {
  var self = this;
  var debounce;

  this.$input.inputchange(function() {
    clearTimeout(debounce);
    debounce = setTimeout(function() {
      self.search();
    }, 300);
  });

  this.$input.on('focus', function() {
    self.search();
  });

  this.$input.on('keydown', function(e) {
    if (Autocomplete.ACTIVE_KEY_CODES.indexOf(e.keyCode) !== -1) {
      self.$input.blur();
      e.preventDefault();
    }
  });
};

/**
 * Add document listeners and trigger a message to show the results
 */
Autocomplete.prototype.doShow = function() {
  this.closeAllOthers();
  this.addDocumentListeners();
  this.message('show');
};

/**
 * Close all other Autocomplete
 */
Autocomplete.prototype.closeAllOthers = function() {
  var self = this;
  Autocomplete.REGISTRY.forEach(function(autocomplete) {
    if (self !== autocomplete) {
      autocomplete.doHide();
    }
  });
};

/**
 * Remove the document listeners and trigger a message to hide the results
 */
Autocomplete.prototype.doHide = function() {
  this.searchText = undefined;
  this.removeDocumentListeners();
  this.$target.empty();
  this.message('hide');
};

/**
 * Clean up event listeners and remove from the registry
 */
Autocomplete.prototype.destroy = function() {
  this.doHide();
  $(this).off();
  var index = Autocomplete.REGISTRY.indexOf(this);
  if (index !== -1) {
    Autocomplete.REGISTRY.splice(index, 1);
  }
};

/**
 * Add document listeners for closing the autocomplete when clicking out of the active area and
 * for accepted keyboard inputs
 * @suppress {deprecated}
 */
Autocomplete.prototype.addDocumentListeners = function() {
  var self = this;
  var $document = $(document);

  this.removeDocumentListeners();

  $document.on('click.autocomplete', function(e) {
    // Hide for any clicks outside of the active region or input
    if (self.$activeRegion.has(e.target).length === 0
        && !self.$input.get(0).isEqualNode(e.target)) {
      self.doHide();
    }
  });

  $document.on('keydown.autocomplete', function(e) {
    var $activeItem = self.$target.find('#' + Autocomplete.ACTIVE_ITEM_ID);

    if (Autocomplete.ACTIVE_KEY_CODES.indexOf(e.keyCode) !== -1) {
      e.preventDefault();
    }

    switch (e.keyCode) {
      case $.ui.keyCode.UP:
        if ($activeItem.prev().length) {
          self.setActive($activeItem.prev(), true);
          self.keyboardMode = true;
        } else {
          self.$input.focus();
        }
        break;
      case $.ui.keyCode.DOWN:
        if ($activeItem.length) {
          if ($activeItem.next().length) {
            $activeItem = $activeItem.next();
          }
        } else {
          $activeItem = self.$target.find('li').first();
        }
        if ($activeItem.length) {
          self.setActive($activeItem, true);
          self.keyboardMode = true;
        }
        break;
      case $.ui.keyCode.ENTER:
        if ($activeItem.length) {
          self.selectResult($activeItem);
        }
        break;
      case $.ui.keyCode.ESCAPE:
        self.doHide();
        break;
    }
  });

  $document.on('mousemove.autocomplete', function(e) {
    self.keyboardMode = false;
  });
};

/**
 * Remove all document listeners by namespace
 */
Autocomplete.prototype.removeDocumentListeners = function() {
  $(document).off('.autocomplete');
};

/**
 * Send a message with the instance as context
 * @param {string} message The message being sent
 * @param {!Object} data Data to send with the message
 */
Autocomplete.prototype.message = function(message, data) {
  $(this).trigger(message, data);
};

/**
 * Do the search by calling the provided search function
 */
Autocomplete.prototype.search = function() {
  var self = this;
  var searchText = this.$input.val();


  if (this.searchText !== searchText) {
    this.searchText = searchText;
    var timeout = this.showLoadingMessage();
    this.source(searchText, function(results) {
      clearTimeout(timeout);
      self.doShow();
      self.renderResults(results);
    });
  }
};

/**
 * Show a message while the results are loading
 */
Autocomplete.prototype.showLoadingMessage = function() {
  var self = this;
  return setTimeout(function() {
    self.doShow();
    self.$target.empty().append(Autocomplete.LOADING_RESULTS.clone());
  }, 300);
};

/**
 * Render the results of the search
 * @param {!Array} results List of results whose format must match {label: '...', value: ...}
 */
Autocomplete.prototype.renderResults = function(results) {
  this.$target.empty();

  var $result;
  if (results.length > 0) {
    var searchRegex = Autocomplete.buildRegex(this.searchText);

    var $ul = Autocomplete.RESULTS_CONTAINER.clone();
    for (var i = 0; i < results.length; i++) {
      var $li = this.renderElement(results[i], searchRegex);
      $ul.append($li);
    }

    $result = $ul;
    this.bindList($result);
  } else {
    if (this.searchText.length === 0) {
      $result = Autocomplete.NO_MORE.clone();
    } else {
      $result = Autocomplete.NO_RESULTS.clone();
    }
  }

  this.$target.append($result);
};

/**
 * Builds a regular expression that treats spaces and underscores the same for better searching
 *
 * @param {string} search The search string
 * @return {RegExp} Regular expression that will match 'search'
 */
Autocomplete.buildRegex = function(search) {
  search = helpers.escapeRegExp($.trim(search));
  search = search.replace(/\s+/g, '([\\s|_])'); // Treat spaces just like underscores
  var regexStr = '^(.*)(' + search + ')(.*)$';
  return new RegExp(regexStr, 'gim');
};

/**
 * Bind the list events
 *
 * @param {!jQuery} $ul Search results container
 */
Autocomplete.prototype.bindList = function($ul) {
  var self = this;
  $ul.on('click', 'li', function() {
    self.selectResult($(this));
  });

  $ul.on('mouseenter', 'li', function() {
    if (!self.keyboardMode) {
      self.setActive($(this));
    }
  });

  $ul.on('mouseleave', 'li', function() {
    if (!self.keyboardMode) {
      $(this).removeAttr('id');
    }
  });
};

/**
 * Set list item to selected
 *
 * @param {!jQuery} $li List item to select
 */
Autocomplete.prototype.selectResult = function($li) {
  this.message('select', $li.data('result'));
};

/**
 * Set list item to active
 *
 * @param {!jQuery} $li List item to set active
 * @param {boolean} scrollTo True to scroll when making the selection (arrow keys)
 */
Autocomplete.prototype.setActive = function($li, scrollTo) {
  $('#' + Autocomplete.ACTIVE_ITEM_ID).removeAttr('id');
  $li.attr({
    id: Autocomplete.ACTIVE_ITEM_ID,
  });

  if (scrollTo) {
    this.$target.find('ul').get(0).scrollTop = $li.get(0).offsetTop;
  }
};

/**
 * Renders the list item, with search term highlighting
 *
 * @param {!Object} result Format must match {label: '...', value: ...}
 * @param {RegExp} searchRegex Regular expression for search term highlighting
 * @return {!jQuery} List item
 */
Autocomplete.prototype.renderElement = function(result, searchRegex) {
  var label = result.label;

  // Search term highlighting
  searchRegex.lastIndex = 0; // Reset the state of the regex
  var matches = searchRegex.exec(result.label);
  if (matches) { // Multi term search a bit harder to highlight
    // This term building is directly related to the regex construction
    label = matches[1] + '<span class="search-term-highlight">' + matches[2] + '</span>' + matches[matches.length - 1];
  }

  var $li = Autocomplete.SEARCH_RESULT.clone();
  $li.data('result', result);
  $li.html(label);
  return $li;
};

exports = Autocomplete;
